import React, { useState } from 'react';
import { Box } from '@mui/material';
import {
  MarketShell, useMarketData, regionLine, LineChartBox,
  REGION_COLORS, REGION_LABELS, NEM_COLOR,
} from './_shared';
import { ChartSection, StatCard, formatMW } from '../charts/shared';

const HOURS = Array.from({ length: 24 }, (_, h) => `${h}:00`);

export default function MarketDemand() {
  const [days, setDays] = useState(365);
  const [region, setRegion] = useState('NEM');
  const { data, loading, error } = useMarketData('demand', days);

  const isNem = region === 'NEM';
  const scopeLabel = isNem ? 'NEM' : (REGION_LABELS[region] || region);
  const lineColor = isNem ? NEM_COLOR : REGION_COLORS[region];

  let body = null;
  if (data) {
    const series = data.series || [];

    // Demand: NEM = sum of regions (additive); region = that region.
    const demand = regionLine(series, {
      region, valField: 'avg_demand', agg: 'sum',
      label: isNem ? 'NEM total' : scopeLabel, color: lineColor,
    });

    // Renewable %: NEM = Σ vre ÷ Σ demand (weighted); region = its renew_pct.
    const renew = regionLine(series, {
      region, valField: 'renew_pct', agg: 'avg',
      label: scopeLabel, color: lineColor,
      nemReducer: (rk) => {
        const sv = rk.reduce((a, r) => a + (r.vre_mw || 0), 0);
        const sd = rk.reduce((a, r) => a + (r.avg_demand || 0), 0);
        return sd ? +(100 * sv / sd).toFixed(2) : null;
      },
    });

    // Duck curve (by hour): NEM = sum across regions; region = that region.
    const duck = data.duck || [];
    const hours = [...new Set(duck.map((d) => d.hour))].sort((a, b) => a - b);
    const pick = (h, f) => {
      const rk = duck.filter((d) => d.hour === h);
      if (!isNem) { const r = rk.find((d) => d.region === region); return r ? r[f] : null; }
      const v = rk.map((d) => d[f]).filter((x) => x != null);
      return v.length ? v.reduce((a, b) => a + b, 0) : null;
    };
    const duckSeries = [
      { data: hours.map((h) => pick(h, 'demand')), label: 'Operational demand',
        color: '#3b82f6', showMark: false, connectNulls: true },
      { data: hours.map((h) => pick(h, 'net')), label: 'Net of wind+solar',
        color: NEM_COLOR, showMark: false, connectNulls: true },
    ];

    // Scope KPIs
    const scopeRows = isNem ? series : series.filter((r) => r.region === region);
    const dates = [...new Set(scopeRows.map((r) => r.date))];
    let avgRenew, avgDemand;
    if (isNem) {
      const sv = scopeRows.reduce((a, r) => a + (r.vre_mw || 0), 0);
      const sd = scopeRows.reduce((a, r) => a + (r.avg_demand || 0), 0);
      avgRenew = sd ? (100 * sv / sd) : null;
      avgDemand = dates.length
        ? scopeRows.reduce((a, r) => a + (r.avg_demand || 0), 0) / dates.length : null;
    } else {
      const rv = scopeRows.map((r) => r.renew_pct).filter((v) => v != null);
      avgRenew = rv.length ? rv.reduce((a, b) => a + b, 0) / rv.length : null;
      const dv = scopeRows.map((r) => r.avg_demand).filter((v) => v != null);
      avgDemand = dv.length ? dv.reduce((a, b) => a + b, 0) / dv.length : null;
    }

    body = (
      <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          <StatCard label={`${scopeLabel} · renewables`}
            value={avgRenew == null ? '—' : `${avgRenew.toFixed(0)}%`}
            unit="avg share" color={lineColor} />
          <StatCard label={`${scopeLabel} · ${isNem ? 'total ' : ''}demand`}
            value={avgDemand == null ? '—' : `${(avgDemand / 1000).toFixed(2)} GW`}
            unit="avg" />
        </Box>

        <ChartSection title={isNem ? 'Total operational demand (NEM)' : `Operational demand — ${scopeLabel}`}
          subtitle={isNem
            ? 'Dispatch TOTALDEMAND summed across regions, daily mean (MW)'
            : 'Dispatch TOTALDEMAND, daily mean (MW)'}>
          <LineChartBox x={demand.x} series={demand.series} yFormat={formatMW} />
        </ChartSection>

        <ChartSection title="Renewable penetration"
          subtitle={isNem
            ? '(Σ scheduled solar + wind UIGF) ÷ Σ demand, daily (%)'
            : `(Scheduled solar + wind UIGF) ÷ demand — ${scopeLabel}, daily (%)`}>
          <LineChartBox x={renew.x} series={renew.series}
            yFormat={(v) => `${Math.round(v)}%`} height={340} />
        </ChartSection>

        <ChartSection title="Average daily shape — the duck curve"
          subtitle={`Demand vs demand net of wind+solar, by hour (AEST), last 30 days — ${scopeLabel}`}>
          <LineChartBox x={hours.length ? hours.map((h) => `${h}:00`) : HOURS}
            series={duckSeries} yFormat={formatMW} height={340} />
        </ChartSection>
      </>
    );
  }

  return (
    <MarketShell
      title="Demand & Renewables"
      subtitle="NEM operational demand and renewable penetration — real AEMO actuals"
      days={days} setDays={setDays} region={region} setRegion={setRegion}
      loading={loading} error={error}
    >
      {body}
    </MarketShell>
  );
}
